import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Banner, FormShell } from "@/components/auth/FormShell";
import formStyles from "@/components/auth/AuthForm.module.css";
import {
  confirmSignUp,
  pendingSignUpEmail,
  resendConfirmationCode,
  type AuthError,
} from "@/lib/auth";

/**
 * 6-digit code verification after sign-up. Cognito emails the code; the
 * user types it here. On success, the PostConfirmation Lambda fires
 * server-side (creates the workspace + AdminDisableUser), then we
 * navigate to /signup/waitlist.
 */
export function SignUpVerify() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryEmail = params.get("email");
  const email = (queryEmail ?? pendingSignUpEmail() ?? "").trim();
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState<AuthError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resentAt, setResentAt] = useState<number | null>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  // No email = the user landed here cold (e.g. opened the URL directly).
  // Punt them back to /signup so we can collect the email + create the
  // pending sign-up that this page needs to confirm.
  useEffect(() => {
    if (!email) navigate("/signup", { replace: true });
  }, [email, navigate]);

  /**
   * Spread a string of digits across the 6 cells starting at offset.
   * When the input is the full code (>= 6 digits), always start at
   * cell 0 — covers the iOS Safari `autocomplete="one-time-code"`
   * autofill case where Safari dumps the entire code into the focused
   * cell, plus the "user pasted 123456 into cell 3" case.
   */
  function spreadInto(offset: number, source: string) {
    const clean = source.replace(/\D/g, "");
    if (!clean) return;
    const start = clean.length >= 6 ? 0 : offset;
    const next = [...digits];
    for (let i = 0; i < clean.length && start + i < 6; i++) {
      next[start + i] = clean[i] ?? "";
    }
    setDigits(next);
    const lastFilled = Math.min(5, start + clean.length - 1);
    inputs.current[lastFilled]?.focus();
  }

  function setDigit(idx: number, raw: string) {
    const clean = raw.replace(/\D/g, "");
    if (!clean) {
      const copy = [...digits];
      copy[idx] = "";
      setDigits(copy);
      return;
    }
    // iOS Safari one-time-code autofill fires onChange (not onPaste)
    // with the full 6-digit code on whichever input it targets — even
    // when maxLength=1 should logically truncate. Handle multi-char
    // input here so autofill survives.
    if (clean.length > 1) {
      spreadInto(idx, clean);
      return;
    }
    const copy = [...digits];
    copy[idx] = clean[0] ?? "";
    setDigits(copy);
    if (idx < 5) inputs.current[idx + 1]?.focus();
  }

  function onKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  }

  function onPaste(idx: number, e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasted.length < 2) return;
    e.preventDefault();
    spreadInto(idx, pasted);
  }

  const code = digits.join("");
  const canSubmit = code.length === 6;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await confirmSignUp(email, code);
      navigate("/signup/waitlist", { replace: true });
    } catch (err) {
      setError(err as AuthError);
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    setError(null);
    try {
      await resendConfirmationCode(email);
      setResentAt(Date.now());
    } catch (err) {
      setError(err as AuthError);
    }
  }

  return (
    <AuthLayout view="signup-verify">
      <FormShell
        title="Enter your verification code"
        subtitle={
          <>
            We sent a 6-digit code to{" "}
            <span className={formStyles.emailEmphasis}>{email}</span>. It expires in 30 minutes.
          </>
        }
      >
        <form onSubmit={onSubmit} noValidate className={formStyles.stack}>
          {error && (
            <Banner variant="danger" icon={<Icon name="info" size={14} />}>
              {error.message}
            </Banner>
          )}
          {resentAt && !error && (
            <Banner variant="success" icon={<Icon name="check" size={14} />}>
              Sent a fresh code — check your inbox again.
            </Banner>
          )}
          <div className={formStyles.codeInputs} role="group" aria-label="6-digit verification code">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                className={formStyles.codeInput}
                inputMode="numeric"
                autoComplete="one-time-code"
                // Cell 0 accepts up to 6 chars so iOS Safari's
                // one-time-code autofill (which dumps the whole code
                // into the first input) isn't browser-truncated.
                // Cells 1-5 stay maxLength=1 for clean single-digit
                // typing; onPaste still routes multi-digit input via
                // spreadInto regardless of which cell is focused.
                maxLength={i === 0 ? 6 : 1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                onPaste={(e) => onPaste(i, e)}
                aria-label={`Digit ${i + 1}`}
                data-invalid={error?.kind === "code_mismatch" ? "true" : undefined}
              />
            ))}
          </div>
          <Btn
            type="submit"
            variant="primary"
            full
            size="lg"
            loading={submitting}
            disabled={!canSubmit}
            iconRight={<Icon name="arrow-right" size={14} />}
          >
            Verify and join the list
          </Btn>
          <div className={formStyles.actionRow}>
            <Btn type="button" variant="secondary" iconLeft={<Icon name="refresh" size={12} />} onClick={onResend}>
              Resend code
            </Btn>
            <Btn variant="ghost" href="/signup">
              Wrong address?
            </Btn>
          </div>
          <div className={formStyles.backRow}>
            <Link to="/signin" className={formStyles.backLink}>
              <Icon name="arrow-left" size={12} />
              Back to sign in
            </Link>
          </div>
        </form>
      </FormShell>
    </AuthLayout>
  );
}
